import api from "./api";

const isDev = process.env.NODE_ENV === "development";
const log = (...args) => isDev && console.log(...args);
const logWarn = (...args) => console.warn(...args);

const VAPID_KEY_CACHE_KEY = "omniplay_vapid_public_key";
const SUBSCRIPTION_SYNCED_KEY = "omniplay_push_sub_synced";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

class PushNotificationService {
  constructor() {
    this.permission = typeof Notification !== "undefined" ? Notification.permission : "denied";
    this.subscription = null;
    this.vapidPublicKey = null;
    this.isSubscribing = false;
    this.permissionCallbacks = [];

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", this._handleSWMessage.bind(this));
    }
  }

  _handleSWMessage(event) {
    if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
      log("🔔 [PushService] Subscription changed — syncing with backend");
      this.subscription = null;
      this._syncSubscriptionWithBackend(event.data.subscription);
    }
    // NOTIFICATION_CLICK é tratado no NotificationsPopOver via history.push() para não quebrar o SPA
  }

  getPermission() {
    return this.permission;
  }

  onPermissionChange(callback) {
    this.permissionCallbacks.push(callback);
    return () => {
      this.permissionCallbacks = this.permissionCallbacks.filter(cb => cb !== callback);
    };
  }

  _notifyPermissionChange(permission) {
    this.permission = permission;
    this.permissionCallbacks.forEach(cb => {
      try { cb(permission); } catch (e) { /* noop */ }
    });
  }

  async requestPermission() {
    if (typeof Notification === "undefined") {
      this._notifyPermissionChange("denied");
      return "denied";
    }

    if (Notification.permission === "granted") {
      this._notifyPermissionChange("granted");
      return "granted";
    }

    if (Notification.permission === "denied") {
      this._notifyPermissionChange("denied");
      return "denied";
    }

    try {
      const permission = await Notification.requestPermission();
      this._notifyPermissionChange(permission);
      return permission;
    } catch (err) {
      logWarn("🔔 [PushService] Erro ao solicitar permissão:", err);
      return "denied";
    }
  }

  async _fetchVapidPublicKey() {
    if (this.vapidPublicKey) return this.vapidPublicKey;

    const cached = sessionStorage.getItem(VAPID_KEY_CACHE_KEY);
    if (cached) {
      this.vapidPublicKey = cached;
      return cached;
    }

    try {
      const { data } = await api.get("/api/push/vapid-key");
      if (data?.publicKey) {
        this.vapidPublicKey = data.publicKey;
        sessionStorage.setItem(VAPID_KEY_CACHE_KEY, data.publicKey);
        return data.publicKey;
      }
    } catch (err) {
      console.warn("🔔 [PushService] VAPID key não disponível:", err?.message || err);
    }
    return null;
  }

  async subscribe() {
    if (this.isSubscribing) return null;
    this.isSubscribing = true;

    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        console.warn("🔔 [PushService] Push não suportado neste navegador");
        return null;
      }

      const permission = await this.requestPermission();
      console.info("🔔 [PushService] Permissão:", permission);
      if (permission !== "granted") {
        return null;
      }

      const vapidKey = await this._fetchVapidPublicKey();
      console.info("🔔 [PushService] VAPID key:", vapidKey ? "OK (" + vapidKey.substring(0, 10) + "...)" : "NULL");
      if (!vapidKey) {
        return null;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        this.subscription = subscription;
        await this._syncSubscriptionWithBackend(subscription.toJSON());
        console.info("🔔 [PushService] Subscription existente reutilizada");
        return subscription;
      }

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      this.subscription = subscription;
      await this._syncSubscriptionWithBackend(subscription.toJSON());
      console.info("🔔 [PushService] Nova subscription criada:", subscription.endpoint.substring(0, 50) + "...");
      return subscription;
    } catch (err) {
      console.error("🔔 [PushService] Erro ao criar subscription:", err);
      return null;
    } finally {
      this.isSubscribing = false;
    }
  }

  async _syncSubscriptionWithBackend(subscriptionJSON) {
    try {
      await api.post("/api/push/subscribe", { subscription: subscriptionJSON });
      localStorage.setItem(SUBSCRIPTION_SYNCED_KEY, Date.now().toString());
      console.info("🔔 [PushService] Subscription sincronizada com backend");
    } catch (err) {
      console.error("🔔 [PushService] Erro ao sincronizar subscription:", err?.response?.status, err?.response?.data || err?.message);
    }
  }

  async unsubscribe() {
    try {
      if (this.subscription) {
        await this.subscription.unsubscribe();
      } else if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const sub = await registration.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }

      await api.delete("/api/push/unsubscribe").catch(() => {});
      this.subscription = null;
      localStorage.removeItem(SUBSCRIPTION_SYNCED_KEY);
      log("🔔 [PushService] Unsubscribed");
    } catch (err) {
      logWarn("🔔 [PushService] Erro ao unsubscribe:", err);
    }
  }

  async showNotification(title, options = {}) {
    if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
      return false;
    }

    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration && registration.active) {
          try {
            await registration.showNotification(title, {
              icon: "/android-chrome-192x192.png",
              badge: "/favicon-32x32.png",
              renotify: true,
              vibrate: [200, 100, 200],
              ...options,
            });
            return true;
          } catch (swErr) {
            logWarn("🔔 [PushService] Erro no SW showNotification, usando fallback nativo:", swErr);
          }
        }
      }

      // Fallback nativo
      new Notification(title, options);
      return true;
    } catch (err) {
      logWarn("🔔 [PushService] Erro fatal ao mostrar notificação:", err);
      return false;
    }
  }

  async closeNotification(tag) {
    if (!tag) return;
    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const notifications = await registration.getNotifications({ tag });
        notifications.forEach(n => n.close());
      }
    } catch (err) {
      // noop
    }
  }

  async getActiveSubscription() {
    try {
      if (!("serviceWorker" in navigator)) return null;
      const registration = await navigator.serviceWorker.ready;
      return await registration.pushManager.getSubscription();
    } catch {
      return null;
    }
  }

  isSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  }
}

const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
